import { useTranslation } from "react-i18next";
import { PageTabsHeader } from "../../components";
import { useCachedTags, useTellescopeInfiniteQuery } from "@tellescope/tanstack-query";
import { useState } from "react";
import { Button, cn, Dialog, SearchInput } from "@tellescope/ui";
import { useDebouncedValue, useFloatingScrollbar } from "@tellescope/react-hooks";
import { Spinner } from "@tellescope/design-system";
import { AppointmentReminderCard } from "./AppointmentReminderCard";
import { useSearchParams } from "react-router-dom";
import { AppointmentReminderDialogContents } from "./AppointmentReminderDialogContents";
import { RefreshCw } from "lucide-react";
import { HealthieImportDialogContents } from "./HealthieImportDialogContents";

export const AppoinmentRemindersPage = () => {
  const { t } = useTranslation("appointment-reminders");
  const [searchParams, setSearchParams] = useSearchParams();
  const templateIdBeingEdited = searchParams.get("templateId");
  const isImporting = searchParams.get("importing") === "true";
  const [selectedTemplateTags, setSelectedTemplateTags] = useState<Set<string>>(new Set());
  const [templateSearch, setTemplateSearch] = useState("");
  const [debouncedTemplateSearch] = useDebouncedValue(templateSearch);
  const templatesQuery = useTellescopeInfiniteQuery({
    model: "calendar_event_templates",
    method: "getSome",
    request: {
      search: debouncedTemplateSearch
        ? {
            query: debouncedTemplateSearch,
          }
        : undefined,
      limit: 100,
      mdbFilter: {
        healthieTemplateId: { $nin: [null, ""] },
        tags: selectedTemplateTags.size ? { $in: Array.from(selectedTemplateTags) } : undefined,
      },
    },
    fetchAllPages: true,
    select: (data) => data.pages.flat(),
    placeholderData: (prev) => prev,
  });
  const { data: templateTags } = useCachedTags({ model: "calendar_event_templates" });
  const { scrollContainerRef, thumbRef, handleScroll } = useFloatingScrollbar({});

  return (
    <div className="flex flex-col h-full min-h-0 overflow-hidden">
      <PageTabsHeader />
      <section className="shrink-0 p-6 border-b flex justify-between items-center">
        <div className="space-y-2">
          <h1 className="text-3xl font-semibold">{t("Appointment Reminders")}</h1>
          <h3 className="font-light text-muted-foreground" data-testid="templates-count">
            {t("{{count}} appointment types", { count: templatesQuery.data?.length ?? 0 })}
          </h3>
        </div>
        <Button
          onClick={() => {
            setSearchParams((prev) => {
              prev.set("importing", "true");
              return prev;
            });
          }}
          variant="outline"
        >
          <RefreshCw />
          {t("Import from Healthie")}
        </Button>
      </section>
      <section className="overflow-y-hidden px-6 pt-6 @container flex flex-col">
        <div className="space-y-3 pb-4 border-b shrink-0">
          <SearchInput
            wrapperClassName="max-w-[400px]"
            placeholder={t("Search appointment types...")}
            value={templateSearch}
            onChange={(e) => setTemplateSearch(e.target.value)}
            data-testid="appointment-type-search-input"
          />
          <div className="flex items-center gap-2" data-testid="templates-tag-filters">
            {templateTags.map((tag) => (
              <Button
                key={tag}
                data-testid={`templates-tag-filter-${tag}`}
                variant={selectedTemplateTags.has(tag) ? "default" : "outline"}
                className={cn(
                  "rounded-full text-xs font-light h-auto py-1",
                  selectedTemplateTags.has(tag) ? "border" : "text-muted-foreground"
                )}
                onClick={() =>
                  setSelectedTemplateTags((prev) => {
                    const next = new Set(prev);
                    if (prev.has(tag)) {
                      next.delete(tag);
                      return next;
                    } else {
                      next.add(tag);
                      return next;
                    }
                  })
                }
              >
                {tag}
              </Button>
            ))}
            {!!selectedTemplateTags.size && (
              <Button
                variant="ghost"
                className="h-auto py-1 rounded-full text-xs font-light text-muted-foreground"
                onClick={() => setSelectedTemplateTags(new Set<string>())}
                data-testid="templates-tag-filter-clear"
              >
                {t("Clear")}
              </Button>
            )}
          </div>
        </div>
        {templatesQuery.isLoading ? (
          <div className="flex flex-col items-center gap-2 grow" data-testid="templates-loading">
            <Spinner />
            <p className="text-muted-foreground text-sm">{t("Loading...")}</p>
          </div>
        ) : (
          <div className="relative grow min-h-0">
            <div
              ref={scrollContainerRef}
              onScroll={handleScroll}
              data-testid="templates-grid"
              className="grid grid-cols-1 @2xl:grid-cols-2 @4xl:grid-cols-3 @7xl:grid-cols-4 gap-4 py-4 h-full overflow-y-auto [&::-webkit-scrollbar]:hidden"
              style={{ scrollbarWidth: "none" }}
            >
              {templatesQuery.data?.map((template) => (
                <AppointmentReminderCard
                  key={template.id}
                  template={template}
                  onClick={() =>
                    setSearchParams((prev) => {
                      prev.set("templateId", template.id);
                      return prev;
                    })
                  }
                />
              ))}
            </div>
            <div
              ref={thumbRef}
              className="absolute top-0 right-1 w-1.5 bg-black/40 rounded-full pointer-events-none opacity-0 transition-opacity duration-300 z-[10000]"
            />
          </div>
        )}
      </section>
      <Dialog
        open={!!templateIdBeingEdited && !!templatesQuery.data}
        onOpenChange={(open) => {
          if (!open) {
            setSearchParams((prev) => {
              prev.delete("templateId");
              return prev;
            });
          }
        }}
      >
        <AppointmentReminderDialogContents
          templateId={templateIdBeingEdited ?? ""}
          onCancel={() => {
            setSearchParams((prev) => {
              prev.delete("templateId");
              return prev;
            });
          }}
          onSave={() => {
            setSearchParams((prev) => {
              prev.delete("templateId");
              return prev;
            });
          }}
        />
      </Dialog>
      <Dialog
        open={isImporting}
        onOpenChange={(open) => {
          setSearchParams((prev) => {
            if (open) {
              prev.set("importing", "true");
            } else {
              prev.delete("importing");
            }
            return prev;
          });
        }}
      >
        <HealthieImportDialogContents
          onImported={() => {
            setSearchParams((prev) => {
              prev.delete("importing");
              return prev;
            });
          }}
          onCancel={() => {
            setSearchParams((prev) => {
              prev.delete("importing");
              return prev;
            });
          }}
        />
      </Dialog>
    </div>
  );
};
